// Thin wrapper over the device API. Every call carries the Cognito ID token;
// API Gateway validates it before the Lambda ever runs.

import { getIdToken } from './auth';

const BASE = import.meta.env.VITE_API_URL;
export const DEVICE_ID = import.meta.env.VITE_DEVICE_ID || 'lewis-spa-01';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getIdToken();
  if (!token) throw new Error('not signed in');

  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      authorization: token,
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
    },
  });

  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new Error(body?.error ?? `${res.status} ${res.statusText}`);
  }
  return body as T;
}

export type SpaMode = 'idle' | 'pending' | 'ready';

export interface DeviceRow {
  deviceId: string;
  online?: boolean;
  lastSeen?: string;
  firmware?: string;
  gitSha?: string;
  partition?: string;
  idfVersion?: string;
  ssid?: string;
  ip?: string;
  rssi?: number;
  heapFree?: number;
  psramFree?: number;
  uptimeSeconds?: number;
  bootCount?: number;
  resetReason?: string;
  logLevel?: string;
  disconnectReason?: string | null;
}

export interface HistorySample {
  ts: string;
  waterTempC?: number | null;
  setTempC?: number | null;
  targetTempC?: number | null;
  heater?: string | null;
  mode?: string;
  link?: string;
  rrp?: number | null;
  amberPerKwh?: number | null;
  amberDescriptor?: string | null;
  pricePercentile?: number | null;
  ambientC?: number | null;
  apparentC?: number | null;
  windKph?: number | null;
  rainMm?: number | null;
  reason?: string;
}

export type AmberDescriptor =
  | 'negative'
  | 'extremelyLow'
  | 'veryLow'
  | 'low'
  | 'neutral'
  | 'high'
  | 'spike';

export interface AmberInterval {
  type?: string;
  channelType?: string;
  perKwh: number;
  spotPerKwh?: number;
  descriptor?: AmberDescriptor;
  spikeStatus?: 'none' | 'potential' | 'spike';
  renewables?: number;
  estimate?: boolean;
  startTime?: string;
  endTime?: string;
  nemTime?: string;
  duration?: number;
  tariff?: { period?: string; season?: string; demandWindow?: boolean };
  advancedPrice?: { low?: number; predicted?: number; high?: number };
}

export interface AmberPrice {
  ts?: string;
  siteId?: string;
  network?: string;
  intervalLength?: number;
  current?: {
    general?: AmberInterval;
    controlledLoad?: AmberInterval;
    feedIn?: AmberInterval;
  };
  forecast?: {
    general?: AmberInterval[];
    controlledLoad?: AmberInterval[];
    feedIn?: AmberInterval[];
  };
}

export interface PriceInfo {
  region: string;
  current: number | null;
  settlementDate: string | null;
  percentiles: { p10: number; p25: number; p50: number; p75: number; p90: number };
  amber?: AmberPrice | null;
}

export interface Weather {
  tempC?: number;
  apparentC?: number;
  humidity?: number;
  rainMm?: number;
  windKph?: number;
  cloudPct?: number;
  observedAt?: string;
  forecast?: { time: string; tempC: number; apparentC: number }[];
}

export const api = {
  device: () => request<DeviceRow>(`/devices/${DEVICE_ID}`),
  history: (hours = 24) =>
    request<{ samples: HistorySample[] }>(`/devices/${DEVICE_ID}/history?hours=${hours}`),
  price: () => request<PriceInfo>('/price'),
  weather: () => request<Weather>('/weather'),
  logs: (minutes = 30) =>
    request<{ events: { timestamp: number; message: string }[] }>(
      `/devices/${DEVICE_ID}/logs?minutes=${minutes}`
    ),

  setSpa: (desired: Record<string, unknown>) =>
    request<{ desired: Record<string, unknown> }>(`/devices/${DEVICE_ID}/spa`, {
      method: 'POST',
      body: JSON.stringify(desired),
    }),
  setConfig: (desired: Record<string, unknown>) =>
    request(`/devices/${DEVICE_ID}/config`, {
      method: 'POST',
      body: JSON.stringify(desired),
    }),
  command: (command: string, extra?: Record<string, unknown>) =>
    request<{ accepted: string; requestId: string }>(`/devices/${DEVICE_ID}/command`, {
      method: 'POST',
      body: JSON.stringify({ command, ...extra }),
    }),
  refreshPanel: () =>
    request<{ accepted: string; requestId: string }>(`/devices/${DEVICE_ID}/panel/refresh`, {
      method: 'POST',
    }),
  iotConfig: () =>
    request<{
      identityPoolId: string;
      iotEndpoint: string;
      region: string;
      topic: string;
      panelTopic: string;
    }>(
      '/iot-config'
    ),
};
